<script>
import { mapGetters, mapActions } from 'vuex'
import ProviderProducts from '@/components/products-table'
import DeleteModal from './modals/delete-modal'
import get from 'lodash/get'

export default {
  name: 'User',
  components: {
    ProviderProducts,
    DeleteModal,
  },
  page: {
    title: 'User',
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      user: {},
      loading: false,
      modals: {
        deleteUser: false,
      },
    }
  },
  computed: {
    ...mapGetters('products', ['products']),
    ...mapGetters('me', [
      'canUpdateUser',
      'canCreateVerificationEmail',
      'canCreateWelcomeEmail',
      'canDeleteUser',
    ]),
    status() {
      if (this.user.active) {
        return 'active'
      } else {
        return 'blocked'
      }
    },
    iconName() {
      switch (this.user.status) {
        case 'active':
          return 'check'
        case 'pending':
          return 'time'
        default:
          return 'close'
      }
    },
    providerName() {
      return get(this.user, 'provider.name', '—')
    },
    commands() {
      return [
        this.canUpdateUser && {
          command: 'edit',
          label: 'Edit User Details',
        },
        this.canCreateVerificationEmail && (this.user.authType !== 'google') && {
          command: 'resend-verification-email',
          label: 'Resend Verification Email',
        },
        this.canCreateWelcomeEmail && {
          command: 'resend-welcome-email',
          label: 'Resend Welcome Email',
        },
        this.canDeleteUser && {
          command: 'delete',
          label: 'Delete User',
        },
        // Not implemented in MVP
        // {
        //   command: 'resend-sms',
        //   label: 'Resend SMS OTP Code',
        // },
        // {
        //   command: 'resend-google',
        //   label: 'Regenerate Google Auth Code',
        // },
        // {
        //   command: 'change-password',
        //   label: 'Change Password',
        // },
      ]
    },
    authType() {
      switch (this.user.authType) {
        case 'google':
          return 'Google SSO'
        case 'password':
          return 'Password'
        default:
          return null
      }
    },
  },
  created() {
    this.getData()
  },
  methods: {
    ...mapActions('user', [
      'getUser',
      'resendVerificationEmail',
      'resendWelcomeEmail',
    ]),
    async getData() {
      this.loading = true

      const [error, response] = await this.getUser(this.id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Request error',
          message: error.message,
        })
      } else if (response) {
        this.user = response
      }

      this.loading = false
    },
    handleCommand(command) {
      switch (command) {
        case 'edit':
          this.onEditClick()
          break
        case 'resend-verification-email':
          this.onResendVerificationEmailClick()
          break
        case 'resend-welcome-email':
          this.onResendWelcomeEmailClick()
          break
        case 'delete':
          this.modals.deleteUser = true
          break
        case 'resend-sms':
          break
        case 'resend-google':
          break
        case 'change-password':
          break
      }
    },
    onEditClick() {
      this.$router.push({
        name: 'user-edit',
        params: {
          id: this.user.id,
          user: this.user,
        },
      })
    },
    async onResendVerificationEmailClick() {
      await this.resendVerificationEmail(this.user.id)
        .then(response => {
          this.$notifier({
            response,
            errorTitle: 'Unable to sent verification email',
            successMsg: 'Verification email successfully sent',
          })
          this.loading = false
        })
    },
    async onResendWelcomeEmailClick() {
      await this.resendWelcomeEmail(this.user.id)
        .then(response => {
          this.$notifier({
            response,
            errorTitle: 'Unable to sent welcome email',
            successMsg: 'Welcome email successfully sent',
          })
          this.loading = false
        })
    },
  },
}
</script>

<template>
  <main-layout
    :title="user.fullName"
    :loading="loading"
    :subtitle="`${user.firstName} ${user.lastName}`"
    :back="{
      name: 'users'
    }"
  >
    <div
      slot="header"
      :class="$style.caption"
    >
      <el-dropdown
        trigger="click"
        @command="handleCommand"
      >
        <dropdown-icon />
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item
            v-for="(command, index) in commands"
            :key="index"
            :command="command.command"
          >
            {{ command.label }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <data-box
      header="User Details"
      :status="status"
      :class="$style.box"
    >
      <data-list>
        <dt>Date Created</dt>
        <dd>{{ user.createdAt | dateTime }}</dd>

        <dt>Last Update Date</dt>
        <dd>{{ user.updatedAt | dateTime }}</dd>

        <dt>Last Login Date</dt>
        <dd>{{ user.lastLoginAt | dateTime }}</dd>

        <dt>User ID</dt>
        <dd>{{ user.id }}</dd>

        <dt>First Name</dt>
        <dd>{{ user.firstName }}</dd>

        <dt>Last Name</dt>
        <dd>{{ user.lastName }}</dd>

        <dt>Email</dt>
        <dd>{{ user.email }}</dd>

        <dt>Telephone</dt>
        <dd>{{ user.phone }}</dd>

        <dt>Provider</dt>
        <dd>{{ providerName }}</dd>

        <dt>Auth Type</dt>
        <dd>{{ authType }}</dd>
      </data-list>

      <hr class="divider-primary info-block__divider">

      <provider-products
        :class="$style.products"
        :products="user.products"
        :roles="user.roles"
      />


      <alert-block
        v-if="user.authType === 'google'"
        type="warning"
      >
        This User is authenticated via Google Single Sign-On.
        Changes to the User’s first name, last name, email address or password must be done via their Google account.
      </alert-block>
    </data-box>


    <delete-modal
      v-if="modals.deleteUser"
      :user="user"
      :visible.sync="modals.deleteUser"
      @close-modal="modals.deleteUser = false"
    />
  </main-layout>
</template>

<style lang="scss" module>
.box {
  position: relative;
}

.header {
  display: flex;
  align-items: center;
}

.headerTitle {
  margin-right: rem(64px);
}

.headerStatus {
  font-size: rem(14px);
  font-weight: normal;
}

.headerStatusActive {
  color: #29d737;
}

.headerStatusPending {
  color: #fbb241;
}

.headerStatusBlocked {
  color: #fc1e1e;
}
</style>
